import React from 'react';
import classNames from 'classnames';
import { Observe } from 'rewire-core';
import { ICell } from 'rewire-grid';
import { Theme, CheckField } from 'rewire-ui';
import DeleteIcon from '@material-ui/icons/Delete';
import ErrorIcon from '@material-ui/icons/Error';
import BatchStatusLabel from './BatchStatusLabel';
import ExportStatusLabel from './ExportStatusLabel';
import { withStyles } from '../utility/styles';

const styles = (theme: Theme) => ({
  cellWrapper: {
    position: 'absolute',
    left: '0',
    top: '0',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'darkgray',
    fontSize: '16px',
    textAlign: 'center',
  },
  cellPointerWrapper: {
    cursor: 'pointer',
  },
  deleteCellIcon: {
    '&:hover': {
      color: theme.palette.error.main,
    },
  },
  errorCellIcon: {
    color: 'red',
    fontSize: '16px',
  },
});

interface ICellRendererProps {
  cell: ICell;
  classes?: any;
}

export const ErrorIconRenderer: React.SFC<{ cell: ICell }> = React.memo(
  withStyles(styles, (props: ICellRendererProps) => {
    return (
      <Observe
        render={() => {
          const { cell, classes } = props;
          if (!cell.value) return null;
          return (
            <div className={classes.cellWrapper}>
              <ErrorIcon className={classes.errorCellIcon} />
            </div>
          );
        }}
      />
    );
  }),
);

export const BatchStatusRenderer: React.SFC<{ cell: ICell }> = React.memo(
  withStyles(styles, (props: ICellRendererProps) => {
    return (
      <Observe
        render={() => {
          const { cell, classes } = props;
          if (!cell.value) return null;
          const showLabel = !!cell.column.typeOptions.showLabel;

          return (
            <div className={classes.cellWrapper}>
              <BatchStatusLabel batchStatus={cell.value} showLabel={showLabel} />
            </div>
          );
        }}
      />
    );
  }),
);

export const ExportStatusRenderer: React.SFC<{ cell: ICell }> = React.memo(
  withStyles(styles, (props: ICellRendererProps) => {
    return (
      <Observe
        render={() => {
          const { cell, classes } = props;
          if (!cell.value) return null;
          const showLabel = !!cell.column.typeOptions.showLabel;

          return (
            <div className={classes.cellWrapper}>
              <ExportStatusLabel exportStatus={cell.value} showLabel={showLabel} />
            </div>
          );
        }}
      />
    );
  }),
);

export const TimecardStatusRenderer: React.SFC<{ cell: ICell }> = React.memo(
  withStyles(styles, (props: ICellRendererProps) => {
    return (
      <Observe
        render={() => {
          const { cell } = props;
          if (!cell.value) return null;
          let color = '#241b1a';
          switch (cell.value) {
            case 'accepted':
              color = '#669639';
              break;
            case 'approved':
              color = '#436325';
              break;
          }
          return (
            <span style={{ flex: '1', color, fontWeight: 'bold', overflow: 'hidden', textAlign: cell.align }}>{cell.value}</span>
          );
        }}
      />
    );
  }),
);

export const TimecardStatusIconRenderer: React.SFC<{ cell: ICell }> = React.memo(
  withStyles(styles, (props: ICellRendererProps) => {
    return (
      <Observe
        render={() => {
          const { cell } = props;
          if (!cell.value) return null;
          let value = cell.value.toString().toLowerCase();
          let color = '#241b1a';
          switch (value) {
            case 'onhold':
              color = '#d63065';
              value = '🛑';
              break;
            case 'none':
              color = '#241b1a';
              value = ' ';
              break;
            case 'accepted':
              color = '#669639';
              value = '✔';
              break;
            case 'approved':
              color = '#00cc48';
              value = '✔';
              break;
            default:
              color = '#241b1a';
              value = value.substring(0, 1).toUpperCase();
              break;
          }
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <span
                style={{
                  flex: '1',
                  color,
                  fontSize: 16,
                  fontWeight: 'bold',
                  overflow: 'hidden',
                  textAlign: 'center',
                }}
              >
                {value}
              </span>
            </div>
          );
        }}
      />
    );
  }),
);

export const DeleteIconRenderer: React.SFC<{ cell: ICell }> = React.memo(
  withStyles(styles, (props: ICellRendererProps) => {
    return (
      <Observe
        render={() => {
          const { cell, classes } = props;
          if (cell.value || cell.readOnly) return null;
          return (
            <button
              type="button"
              className={classNames(classes.cellWrapper, classes.cellPointerWrapper)}
              onClick={cell.column.typeOptions.onClick(cell)}
              aria-label="Delete row"
              style={{ background: 'transparent', border: 'none', padding: 0 }}
            >
              <DeleteIcon className={classes.deleteCellIcon} />
            </button>
          );
        }}
      />
    );
  }),
);

export const CheckedIconRenderer: React.SFC<{ cell: ICell }> = React.memo(
  withStyles(styles, (props: ICellRendererProps) => {
    const { cell, classes } = props;
    return (
      <Observe
        render={() => {
          return (
            <CheckField
              className={classes.cellWrapper}
              checked={cell.value}
              value={cell.value}
              onValueChange={() => (cell.value = !cell.value)}
            />
          );
        }}
      />
    );
  }),
);

export const GroupTooltipCellRenderer: React.SFC<{ cell: ICell }> = React.memo(
  withStyles(styles, (props: ICellRendererProps) => {
    return (
      <Observe
        render={() => {
          const { cell } = props;
          if (!cell.value) return null;

          const map = cell.column.map;
          const value = map ? map(cell.value) : cell.value.toString();
          const tooltip = cell.value?.area?.name;

          return (
            <div title={tooltip}>
              <span>{value}</span>
            </div>
          );
        }}
      />
    );
  }),
);
